import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  RefreshControl,
  Linking,
  Platform,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { Button, Surface } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { RootStackParamList } from '../types/navigation';
import { Resource } from '../types/resource';
import resourceService from '../services/resourceService';
import authService from '../services/authService';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import LoadingImage from '../components/LoadingImage';
import SlidingMenu from '../components/SlidingMenu';
import * as DocumentPicker from 'expo-document-picker';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type ResourcesScreenNavigationProp = NativeStackNavigationProp<RootStackParamList, 'Resources'>;

const cardShadow = Platform.OS === 'web'
  ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
  : {
      shadowColor: '#0F172A',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
    };

const ResourcesScreen: React.FC = () => {
  const navigation = useNavigation<ResourcesScreenNavigationProp>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [resources, setResources] = useState<Resource[]>([]);
  const [savedResources, setSavedResources] = useState<Resource[]>([]);
  const [activeTab, setActiveTab] = useState<'my-resources' | 'saved'>('my-resources');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    checkAuthAndLoadResources();
  }, []);

  const checkAuthAndLoadResources = async () => {
    try {
      let isLoggedIn = await authService.isLoggedIn();

      if (!isLoggedIn) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        isLoggedIn = await authService.isLoggedIn();
      }

      if (!isLoggedIn) {
        Alert.alert('Authentication Required', 'Please log in to access resources');
        navigation.navigate('Login');
        return;
      }

      await loadResources();
    } catch (error) {
      console.error('Error checking auth:', error);
      Alert.alert('Error', 'Failed to verify authentication');
    }
  };

  const loadResources = async () => {
    try {
      setLoading(true);

      const [myResources, saved] = await Promise.all([
        resourceService.getMyResources(),
        resourceService.getSavedResources(),
      ]);

      setResources(myResources);
      setSavedResources(saved);
    } catch (error: unknown) {
      console.error('Error loading resources:', error);
      const message = error instanceof Error ? error.message : '';
      if (message.includes('Authentication required')) {
        Alert.alert('Authentication Required', 'Please log in again');
        navigation.navigate('Login');
      } else {
        Alert.alert('Error', 'Failed to load resources');
      }
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadResources();
    setRefreshing(false);
  };

  const handleAddResource = () => {
    navigation.navigate('AddResource');
  };

  const handleOpenResource = async (resource: Resource) => {
    try {
      if (resource.type === 'document' && Platform.OS !== 'web') {
        try {
          const authenticatedUrl = await resourceService.getAuthenticatedResourceUrl(resource);

          if (Platform.OS === 'ios') {
            const safariUrl = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(authenticatedUrl)}`;
            const supported = await Linking.canOpenURL(safariUrl);
            if (supported) {
              await Linking.openURL(safariUrl);
            } else {
              await Linking.openURL(authenticatedUrl);
            }
          } else {
            await Linking.openURL(authenticatedUrl);
          }
        } catch (error) {
          console.error('Error opening document:', error);
          Alert.alert('Error', 'Failed to open document. Please try again.');
        }
      } else if (resource.type === 'document' && Platform.OS === 'web') {
        try {
          const token = await authService.getAuthToken();
          if (!token) {
            Alert.alert('Error', 'Please log in to view documents');
            return;
          }

          const newWindow = window.open('', '_blank');
          if (newWindow) {
            const response = await fetch(resourceService.getResourceUrl(resource), {
              headers: {
                'x-auth-token': token,
              },
            });

            if (response.ok) {
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);

              newWindow.document.write(`
                <html>
                  <head>
                    <title>${resource.title || 'Document'}</title>
                    <style>
                      body { margin: 0; padding: 0; }
                      iframe { width: 100%; height: 100vh; border: none; }
                    </style>
                  </head>
                  <body>
                    <iframe src="${blobUrl}" type="application/pdf"></iframe>
                  </body>
                </html>
              `);
              newWindow.document.close();

              setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
            } else {
              newWindow.close();
              Alert.alert('Error', 'Failed to load document');
            }
          } else {
            Alert.alert('Error', 'Please allow popups to view documents');
          }
        } catch (error) {
          console.error('Error opening document:', error);
          Alert.alert('Error', 'Failed to open document');
        }
      } else {
        const url = resourceService.getResourceUrl(resource);
        const supported = await Linking.canOpenURL(url);

        if (supported) {
          await Linking.openURL(url);
        } else {
          Alert.alert('Error', 'Cannot open this resource');
        }
      }
    } catch (error) {
      console.error('Error opening resource:', error);
      Alert.alert('Error', 'Failed to open resource');
    }
  };

  const handleToggleSave = async (resource: Resource) => {
    try {
      const resourceId = resource.id || resource._id;
      if (!resourceId) {
        Alert.alert('Error', 'Resource ID not found');
        return;
      }

      if (resource.savedBy?.length) {
        await resourceService.unsaveResource(resourceId);
      } else {
        await resourceService.saveResource(resourceId);
      }

      await loadResources();
    } catch (error) {
      console.error('Error toggling save:', error);
      Alert.alert('Error', 'Failed to update resource');
    }
  };

  const handleDeleteResource = async (resource: Resource) => {
    const resourceId = resource.id || resource._id;
    if (!resourceId) {
      Alert.alert('Error', 'Resource ID not found');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this resource?');
    if (!confirmed) {
      return;
    }

    try {
      await resourceService.deleteResource(resourceId);
      await loadResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      Alert.alert('Error', 'Failed to delete resource');
    }
  };

  const handleUploadDocument = async () => {
    try {
      setUploading(true);

      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      const title = window.prompt('Enter a title for this document:');
      if (!title) {
        setUploading(false);
        return;
      }

      const description = window.prompt('Enter a description (optional):') || '';

      try {
        const fileInfo = {
          uri: file.uri,
          name: file.name,
          type: file.mimeType || 'application/pdf',
        };

        const uploadResponse = await resourceService.uploadFile(fileInfo);

        const resourceData = {
          title,
          description,
          type: 'document' as const,
          url: uploadResponse.fileUrl,
        };

        await resourceService.createResource(resourceData);
        await loadResources();

        Alert.alert('Success', 'Document uploaded successfully!');
      } catch (error) {
        console.error('Error uploading document:', error);
        Alert.alert('Error', 'Failed to upload document');
      } finally {
        setUploading(false);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document');
    } finally {
      setUploading(false);
    }
  };

  const getResourceIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'document':
        return 'document-text-outline';
      case 'link':
        return 'link-outline';
      case 'video':
        return 'videocam-outline';
      default:
        return 'document-outline';
    }
  };

  const renderResourceItem = ({ item }: { item: Resource }) => (
    <Surface style={styles.resourceCard} elevation={0}>
      <View style={styles.resourceIcon}>
        <Ionicons name={getResourceIcon(item.type)} size={22} color={brandPrimary} />
      </View>

      <View style={styles.resourceContent}>
        <Text style={styles.resourceTitle} numberOfLines={1}>
          {item.title}
        </Text>
        {item.description ? (
          <Text style={styles.resourceDescription} numberOfLines={2}>
            {item.description}
          </Text>
        ) : null}
      </View>

      <View style={styles.resourceActions}>
        {item.url ? (
          <TouchableOpacity style={styles.actionButton} onPress={() => handleOpenResource(item)}>
            <Ionicons name="open-outline" size={18} color={brandPrimary} />
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity style={styles.actionButton} onPress={() => handleToggleSave(item)}>
          <Ionicons
            name={item.savedBy?.length ? 'bookmark' : 'bookmark-outline'}
            size={18}
            color={item.savedBy?.length ? brandPrimary : '#64748B'}
          />
        </TouchableOpacity>

        {activeTab === 'my-resources' ? (
          <TouchableOpacity style={styles.actionButton} onPress={() => handleDeleteResource(item)}>
            <Ionicons name="trash-outline" size={18} color="#d32f2f" />
          </TouchableOpacity>
        ) : null}
      </View>
    </Surface>
  );

  const currentResources = activeTab === 'my-resources' ? resources : savedResources;
  const toggleMenu = () => setIsMenuVisible((v) => !v);

  const listHeader = (
    <>
      <Text style={styles.screenLabel}>Teaching materials</Text>
      <Text style={styles.screenTitle}>Resources</Text>
      <Text style={styles.subtitle}>
        Save and share links, videos, and documents with fellow educators.
      </Text>

      <View style={styles.tabRow}>
        <Pressable
          style={[styles.tab, activeTab === 'my-resources' && styles.tabActive]}
          onPress={() => setActiveTab('my-resources')}
        >
          <Text style={[styles.tabText, activeTab === 'my-resources' && styles.tabTextActive]}>
            My resources
          </Text>
        </Pressable>
        <Pressable
          style={[styles.tab, activeTab === 'saved' && styles.tabActive]}
          onPress={() => setActiveTab('saved')}
        >
          <Text style={[styles.tabText, activeTab === 'saved' && styles.tabTextActive]}>Saved</Text>
        </Pressable>
      </View>

      {activeTab === 'my-resources' ? (
        <View style={styles.actionRow}>
          <Button
            mode="contained"
            onPress={handleAddResource}
            style={styles.addButton}
            buttonColor={brandButton}
            textColor="#ffffff"
            icon="plus"
          >
            Add resource
          </Button>
          <Button
            mode="outlined"
            onPress={handleUploadDocument}
            disabled={uploading}
            loading={uploading}
            style={styles.uploadButton}
            textColor={brandPrimary}
          >
            {uploading ? 'Uploading…' : 'Upload PDF'}
          </Button>
        </View>
      ) : null}
    </>
  );

  const listEmpty = (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyEmoji}>📚</Text>
      <Text style={styles.emptyTitle}>
        {activeTab === 'my-resources' ? 'No resources yet' : 'No saved resources'}
      </Text>
      <Text style={styles.emptyText}>
        {activeTab === 'my-resources'
          ? 'Add your first link, video, or document to get started.'
          : 'Bookmark resources from the community to find them here.'}
      </Text>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.container}>
        <Surface style={styles.surface}>
          <View style={styles.headerWrap}>
            <HeaderBar
              rightContent={
                <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                  <Text style={styles.menuText}>☰</Text>
                </TouchableOpacity>
              }
              onLogoPress={() => navigation.navigate('Home')}
              style={styles.headerBar}
            />
          </View>
          <View style={styles.loadingContainer}>
            <LoadingImage />
          </View>
        </Surface>
        <SlidingMenu isVisible={isMenuVisible} onClose={() => setIsMenuVisible(false)} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <FlatList
            data={currentResources}
            renderItem={renderResourceItem}
            keyExtractor={(item) => item.id || item._id || item.title}
            ListHeaderComponent={listHeader}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={brandPrimary} />
            }
            ListEmptyComponent={listEmpty}
          />
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={() => setIsMenuVisible(false)} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
    gap: 12,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 10,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 24,
  },
  tabRow: {
    flexDirection: 'row',
    marginBottom: 20,
    backgroundColor: '#FFFFFF',
    borderRadius: 999,
    padding: 4,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 12px rgba(15, 23, 42, 0.04)' }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
          elevation: 1,
        }),
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabActive: {
    backgroundColor: brandButton,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#64748B',
  },
  tabTextActive: {
    color: '#FFFFFF',
  },
  actionRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
  },
  addButton: {
    flex: 1,
    borderRadius: 999,
  },
  uploadButton: {
    flex: 1,
    borderRadius: 999,
    borderColor: brandPrimary,
  },
  resourceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  resourceIcon: {
    width: 44,
    height: 44,
    backgroundColor: brandPrimarySoft,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  resourceContent: {
    flex: 1,
    marginRight: 8,
  },
  resourceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  resourceDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  resourceActions: {
    flexDirection: 'row',
    gap: 6,
  },
  actionButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
  },
  emptyEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 15,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 22,
  },
});

export default ResourcesScreen;
